import { useState } from 'react';
import { ArrowBigUp, ArrowRight, ChevronDown, CornerDownLeft, Delete, Eraser, Space } from 'lucide-react';

export type KbdMode = 'numeric' | 'full';

interface Props {
    mode: KbdMode;
    /** Applique une transformation sur la valeur du champ (update fonctionnel). */
    apply: (fn: (v: string) => string) => void;
    /** Valeur courante affichée dans la barre d'aperçu au-dessus du clavier. */
    preview?: string;
    /** Libellé du champ actif affiché dans la barre d'aperçu. */
    previewLabel?: string;
    /** 'next' → touche « Suivant » (enchaîne les champs) ; 'done' → entrée/validation. */
    enterHint?: 'next' | 'done';
    onEnter?: () => void;
    onClose: () => void;
}

const DIGITS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'];
const ROW1 = ['a', 'z', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p'];
const ROW2 = ['q', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l', 'm'];
const ROW3 = ['w', 'x', 'c', 'v', 'b', 'n'];
const SYMBOLS = ['-', '_', '@', '.', ',', '/', ':', ';', "'", '+'];
const NUMERIC_GRID = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '-', '0', '.'];

/**
 * Clavier virtuel complet de la borne — se comporte comme un vrai clavier :
 * - Mises à jour fonctionnelles (les frappes s'ajoutent au texte, pas de remplacement)
 * - Majuscules/minuscules via touche Shift
 * - Rangée chiffres + symboles + espace + retour arrière + Effacer + Entrée
 * - Mode 'numeric' : pavé 3×4 pour les champs purement numériques
 * Affiché en bas d'écran au focus d'un champ (inputMode="none" côté input
 * → le clavier natif de l'appareil ne s'ouvre jamais).
 */
const BorneKeyboard = ({ mode, apply, preview, previewLabel, enterHint, onEnter, onClose }: Props) => {
    const [shift, setShift] = useState(false)
    const [caps, setCaps] = useState(false)
    const [symbols, setSymbols] = useState(false)

    const type = (k: string) => {
        apply((v) => v + k)
        if (shift) setShift(false) // maj simple = une lettre ; caps verrouillé persiste
    }

    /** Clic = maj une lettre · Double-clic = verrou majuscules · Re-clic = off */
    const onShiftClick = () => { setCaps(false); setShift((s) => !s) }
    const onShiftDouble = () => { setShift(false); setCaps(true) }
    const backspace = () => apply((v) => v.slice(0, -1))
    const clear = () => apply(() => '')
    const enter = () => { if (onEnter) onEnter(); onClose() }

    const Key = ({ k, cls, children }: { k?: string; cls?: string; children?: any }) => (
        <button
            type="button"
            className={`eb-kbd-key${cls ? ' ' + cls : ''}`}
            onClick={k != null ? () => type(k) : undefined}
        >{children ?? k}</button>
    );

    /* Barre d'aperçu de la saisie, collée au-dessus des touches */
    const PreviewBar = preview === undefined ? null : (
        <div className="eb-kbd-preview">
            {previewLabel && <span className="eb-kbd-preview-label">{previewLabel}</span>}
            <span className={`eb-kbd-preview-text${preview ? '' : ' empty'}`}>
                {preview || 'Tapez ici…'}
            </span>
            <span className="eb-kbd-caret" />
        </div>
    )

    /* ---------------------- Pavé numérique ---------------------- */
    if (mode === 'numeric') {
        return (
            <div className="eb-kbd">
                {PreviewBar}
                <div className="eb-kbd-head">
                    <span className="eb-kbd-title">Pavé numérique</span>
                    <button className="eb-kbd-close" onClick={onClose}><ChevronDown size={18} /></button>
                </div>
                <div className="eb-kbd-num">
                    {NUMERIC_GRID.map((k) => <Key key={k} k={k} cls="num" />)}
                </div>
                <div className="eb-kbd-row">
                    <button type="button" className="eb-kbd-key act" onClick={clear}><Eraser size={18} /></button>
                    <button type="button" className="eb-kbd-key" onClick={() => type(' ')}><Space size={18} /></button>
                    <button type="button" className="eb-kbd-key act" onClick={backspace}><Delete size={18} /></button>
                    <button type="button" className="eb-kbd-key ok" onClick={enter}>
                        {enterHint === 'next' ? <><ArrowRight size={17} /> Suivant</> : <><CornerDownLeft size={17} /> Entrée</>}
                    </button>
                </div>
            </div>
        );
    }

    /* ---------------------- Clavier complet ---------------------- */
    const letter = (l: string) => (shift || caps ? l.toUpperCase() : l)

    return (
        <div className="eb-kbd">
            {PreviewBar}
            <div className="eb-kbd-head">
                <span className="eb-kbd-title">Clavier{caps ? ' · MAJ' : ''}</span>
                <button className="eb-kbd-close" onClick={onClose}><ChevronDown size={18} /></button>
            </div>

            {/* Chiffres */}
            <div className="eb-kbd-row">
                {DIGITS.map((k) => <Key key={k} k={k} cls="num" />)}
            </div>

            {/* Lettres ou symboles */}
            {symbols ? (
                <div className="eb-kbd-row">
                    {SYMBOLS.map((k) => <Key key={k} k={k} />)}
                </div>
            ) : (
                <>
                    <div className="eb-kbd-row">
                        {ROW1.map((l) => <Key key={l} k={letter(l)} />)}
                    </div>
                    <div className="eb-kbd-row">
                        {ROW2.map((l) => <Key key={l} k={letter(l)} />)}
                    </div>
                </>
            )}
            <div className="eb-kbd-row">
                <button type="button"
                    className={`eb-kbd-key wide${shift || caps ? ' is-shift' : ''}${caps ? ' is-caps' : ''}`}
                    onClick={onShiftClick} onDoubleClick={onShiftDouble}>
                    <ArrowBigUp size={18} />{caps && <span className="eb-kbd-caps-dot" />}
                </button>
                {(symbols ? [] : ROW3).map((l) => <Key key={l} k={letter(l)} />)}
                {symbols && <Key k="=" />}
                <button type="button" className="eb-kbd-key wide act" onClick={backspace}><Delete size={18} /></button>
            </div>

            {/* Ligne d'actions */}
            <div className="eb-kbd-row">
                <button type="button" className="eb-kbd-key wide" onClick={() => setSymbols(!symbols)}>
                    {symbols ? 'ABC' : '?!#'}
                </button>
                <button type="button" className="eb-kbd-key act" onClick={clear}><Eraser size={18} /></button>
                <button type="button" className="eb-kbd-key space" onClick={() => type(' ')}>
                    <Space size={16} /> Espace
                </button>
                <button type="button" className="eb-kbd-key ok" onClick={enter}>
                    {enterHint === 'next' ? <><ArrowRight size={16} /> Suivant</> : <CornerDownLeft size={16} />}
                </button>
            </div>
        </div>
    );
};

export default BorneKeyboard;
